import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Link,
} from '@mui/material';
import { CloudUpload, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { authService } from '../services/authService';

const W9UploadPage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [w9Document, setW9Document] = useState<string>('');
  const [w9Uploading, setW9Uploading] = useState(false);
  const [w9Details, setW9Details] = useState({
    taxId: '',
    businessName: '',
    businessType: 'individual' as 'individual' | 'llc' | 'corporation' | 'partnership' | 'other',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Redirect if not a provider
    if (user && user.userType !== 'provider') {
      navigate('/');
    }
    
    // If W-9 already on file, show success message
    if (user && (user as any).w9OnFile) {
      setSuccess(true);
    }
  }, [user, navigate]);

  const handleW9Upload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (PDF only for W-9)
    if (file.type !== 'application/pdf') {
      setError('W-9 form must be a PDF file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('W-9 document must be smaller than 10MB');
      return;
    }

    try {
      setW9Uploading(true);
      setError('');

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        setW9Document(base64String);
        setW9Uploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError(err.message || 'Failed to upload W-9 form');
      setW9Uploading(false);
    }
  };

  const handleW9DetailsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    setW9Details({
      ...w9Details,
      [target.name]: target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!w9Document) {
      setError('W-9 form is required');
      setLoading(false);
      return;
    }

    if (!w9Details.taxId) {
      setError('Tax ID (EIN or SSN) is required');
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.put('/providers/w9', {
        w9Document,
        w9TaxId: w9Details.taxId,
        w9BusinessName: w9Details.businessName || (user as any)?.businessName || '',
        w9BusinessType: w9Details.businessType,
        w9OnFile: true,
        w9SubmittedDate: new Date().toISOString(),
      });

      if (response.success) {
        setSuccess(true);
        // Refresh user data to update W-9 status
        try {
          const updatedUser = await authService.getCurrentUser();
          await updateUser(updatedUser);
        } catch (err) {
          console.error('Failed to refresh user data:', err);
          // Continue anyway - user will see updated data on next page load
        }
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/provider/dashboard');
        }, 2000);
      } else {
        setError(response.error || 'Failed to submit W-9 form');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit W-9 form');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            W-9 Form Submitted Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your W-9 form has been received. Payouts will be processed once your account is verified.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/provider/dashboard')}>
            Return to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          W-9 Tax Form
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            IRS requires a quick W-9 before we can pay you — takes 30 seconds.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Download a blank W-9 form from{' '}
            <Link href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" target="_blank" rel="noopener noreferrer">
              IRS.gov
            </Link>
            {' '}, fill it out, sign it, and upload it here.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <input
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  id="w9-upload"
                  type="file"
                  onChange={handleW9Upload}
                  disabled={w9Uploading || loading}
                />
                <label htmlFor="w9-upload">
                  <Button
                    variant="contained"
                    component="span"
                    color={w9Document ? 'success' : 'primary'}
                    startIcon={w9Uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                    disabled={w9Uploading}
                    fullWidth
                    sx={{ py: 1.5 }}
                  >
                    {w9Uploading ? 'Uploading...' : w9Document ? 'W-9 Form Uploaded ✓' : 'Upload W-9 Form (Required)'}
                  </Button>
                </label>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  PDF only, max 10MB. Must be signed.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="w9TaxId"
                label="Tax ID (EIN or SSN)"
                name="taxId"
                placeholder="XX-XXXXXXX"
                value={w9Details.taxId}
                onChange={handleW9DetailsChange}
                disabled={loading}
                helperText="Enter your EIN (business) or SSN (individual)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Business Type</InputLabel>
                <Select
                  name="businessType"
                  value={w9Details.businessType}
                  label="Business Type"
                  onChange={(e) => setW9Details({ ...w9Details, businessType: e.target.value as any })}
                  disabled={loading}
                >
                  <MenuItem value="individual">Individual/Sole Proprietor</MenuItem>
                  <MenuItem value="llc">LLC</MenuItem>
                  <MenuItem value="corporation">Corporation (C-Corp or S-Corp)</MenuItem>
                  <MenuItem value="partnership">Partnership</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="w9BusinessName"
                label="Business Name (if different from profile)"
                name="businessName"
                placeholder="Leave blank if same as business name in your profile"
                value={w9Details.businessName}
                onChange={handleW9DetailsChange}
                disabled={loading}
                helperText="Only if different from the business name in your profile"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/provider/dashboard')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !w9Document || !w9Details.taxId}
                >
                  {loading ? <CircularProgress size={24} /> : 'Submit W-9 Form'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default W9UploadPage;

